import { ProgressBar as ReactAriaProgressBar } from "react-aria-components";

import { cn } from "#utils";

export interface CircularProgressProps {
  /** The progress value as a percentage (0-100). If not provided, shows a spinning loader */
  value?: number;
  /** Size of the circular progress */
  size?: "small" | "medium" | "large" | "xl";
  /** Color variant - inverted uses white/light colors for dark backgrounds */
  variant?: "default" | "inverted";
  /** Optional aria-label for accessibility */
  "aria-label"?: string;
}

export function CircularProgress({
  value,
  size = "medium",
  variant = "default",
  "aria-label": ariaLabel = "Loading…",
}: CircularProgressProps) {
  // Map size strings to pixel values
  const sizeMap = {
    small: 14,
    medium: 24,
    large: 32,
    xl: 48,
  };
  const pixelSize = sizeMap[size];
  const strokeWidth = 4;
  const center = 16;
  const r = center - strokeWidth;
  const c = 2 * r * Math.PI;

  // Choose colors based on variant
  const colorClass = variant === "inverted" ? "text-neutral-white" : "text-foreground-brand";

  // If no value is provided, show spinning indeterminate loader
  if (value === undefined) {
    return (
      <div aria-label={ariaLabel} aria-valuenow={undefined} role="progressbar">
        <svg
          className={cn("animate-spin", colorClass)}
          fill="none"
          height={pixelSize}
          viewBox="0 0 32 32"
          width={pixelSize}
        >
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            fill="none"
            opacity={0.2}
            r={r}
            stroke="currentColor"
            strokeWidth={strokeWidth}
          />
          {/* Spinning arc */}
          <circle
            cx={center}
            cy={center}
            fill="none"
            r={r}
            stroke="currentColor"
            strokeDasharray={`${c * 0.25} ${c * 0.75}`}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
          />
        </svg>
      </div>
    );
  }

  // Show determinate progress bar
  return (
    <ReactAriaProgressBar aria-label={ariaLabel} value={value}>
      {({ percentage }) => (
        <svg
          className={colorClass}
          fill="none"
          height={pixelSize}
          strokeWidth={strokeWidth}
          viewBox="0 0 32 32"
          width={pixelSize}
        >
          {/* Inner border circle */}
          <circle
            cx={center}
            cy={center}
            fill="none"
            opacity={0.3}
            r={r - (strokeWidth / 2 - 0.25)}
            stroke="currentColor"
            strokeWidth={0.5}
          />
          {/* Outer border circle */}
          <circle
            cx={center}
            cy={center}
            fill="none"
            opacity={0.3}
            r={r + (strokeWidth / 2 - 0.25)}
            stroke="currentColor"
            strokeWidth={0.5}
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            fill="none"
            r={r}
            stroke="currentColor"
            strokeDasharray={`${c} ${c}`}
            strokeDashoffset={c - ((percentage ?? 0) / 100) * c}
            strokeLinecap="round"
            strokeWidth={strokeWidth}
            transform="rotate(-90 16 16)"
          />
        </svg>
      )}
    </ReactAriaProgressBar>
  );
}
